import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SolicitudesDataSource } from '../../features/solicitudes/solicitudes-data-source';
import { ResolverSolicitudInput, ResumenDiscipulos } from '../../features/solicitudes/solicitudes.model';
import { mapUserToChat } from '../../shared/helpers/map-user-to-chat';
import { ChatConversacion } from '../../shared/interfaces/chat.interface';
import {
  CrearSolicitudTipo,
  SolicitudesApiResponse,
} from '../../shared/interfaces/solicitud-user.interface';
import { skipLoading } from '../interceptors/skip-loading.context';
import { UserStore } from './user-store.service';

@Injectable({ providedIn: 'root' })
export class SolicitudesService extends SolicitudesDataSource {
  private readonly http = inject(HttpClient);
  private readonly userStore = inject(UserStore);
  private readonly apiUrl = `${environment.apiUrl}/solicitudes`;

  listar(): Observable<ChatConversacion[]> {
    const path = this.userStore.isAdmin() ? this.apiUrl : `${this.apiUrl}/celula`;
    return this.mapear(this.http.get<SolicitudesApiResponse>(path));
  }

  enviarMensaje(personaId: string, nota: string): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.post<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}/mensajes`, { nota }),
    );
  }

  marcarLeidos(personaId: string): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.put<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}/leido`, {}, {
        context: skipLoading(),
      }),
    );
  }

  cerrarChat(personaId: string): Observable<ChatConversacion[]> {
    return this.mapear(this.http.put<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}/cerrar`, {}));
  }

  cerrarChats(): Observable<ChatConversacion[]> {
    return this.mapear(this.http.put<SolicitudesApiResponse>(`${this.apiUrl}/cerrar-todos`, {}));
  }

  resolverSolicitud(input: ResolverSolicitudInput): Observable<ChatConversacion[]> {
    if (input.tipo === 'eliminar') {
      return this.mapear(
        this.http.post<SolicitudesApiResponse>(`${this.apiUrl}/${input.personaId}/eliminacion`, {
          accion: input.accion,
          motivo: input.motivo ?? '',
          ...(input.discipulos ? { discipulos: input.discipulos } : {}),
          ...(input.idLiderDestino ? { idLiderDestino: input.idLiderDestino } : {}),
        }),
      );
    }

    if (input.tipo === 'bajaBautismo') {
      const accion = input.accion === 'aceptar' ? 'aceptar' : 'rechazar';
      return this.mapear(
        this.http.post<SolicitudesApiResponse>(
          `${this.apiUrl}/${input.personaId}/baja-bautismo/${accion}`,
          { motivo: input.motivo ?? '' },
        ),
      );
    }

    return this.mapear(
      this.http.post<SolicitudesApiResponse>(`${this.apiUrl}/${input.personaId}/resolver`, {
        tipo: input.tipo,
        accion: input.accion,
        motivo: input.motivo ?? '',
      }),
    );
  }

  resumenDiscipulos(personaId: string): Observable<ResumenDiscipulos> {
    return this.http
      .get<{ success: boolean; data?: ResumenDiscipulos }>(`${this.apiUrl}/${personaId}/discipulos`)
      .pipe(map((response) => response.data ?? { lider: false, total: 0 }));
  }

  abrirChat(personaId: string): Observable<ChatConversacion[]> {
    return this.mapear(this.http.put<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}/abrir`, {}));
  }

  crearBajaBautismo(personaId: string, motivo: string): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.post<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}/baja-bautismo`, { motivo }),
    );
  }

  crearSolicitud(
    personaId: string,
    tipo: CrearSolicitudTipo,
    motivo: string,
  ): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.post<SolicitudesApiResponse>(`${this.apiUrl}/${personaId}`, { tipo, motivo }),
    );
  }

  listarCelula(silent = false): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.get<SolicitudesApiResponse>(
        `${this.apiUrl}/celula`,
        silent ? { context: skipLoading() } : {},
      ),
    );
  }

  enviarMensajeCelula(personaId: string, nota: string): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.post<SolicitudesApiResponse>(
        `${this.apiUrl}/${personaId}/mensajes`,
        { nota, origen: 'celula' },
        { context: skipLoading() },
      ),
    );
  }

  marcarLeidosCelula(personaId: string): Observable<ChatConversacion[]> {
    return this.mapear(
      this.http.put<SolicitudesApiResponse>(
        `${this.apiUrl}/${personaId}/leido`,
        {},
        { context: skipLoading(), params: { origen: 'celula' } },
      ),
    );
  }

  private mapear(source: Observable<SolicitudesApiResponse>): Observable<ChatConversacion[]> {
    return source.pipe(map((response) => (response.data ?? []).map(mapUserToChat)));
  }
}
